import React, { useState, useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import {
  ArrowLeft,
  Terminal,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Lightbulb,
} from "lucide-react";
import { FaPython, FaJsSquare } from "react-icons/fa";
import { SiArduino } from "react-icons/si";
import { TbBrandCpp } from "react-icons/tb";
import { LuCopy, LuCheck, LuDownload, LuRefreshCw } from "react-icons/lu";
import { transpileCode, getStandaloneDownloadCode } from "../../../utils/transpiler/codeTranspiler";
import { validateSyntax } from "../../../utils/transpiler/syntaxValidator";

const TARGET_LANGUAGES = [
  { id: "python", label: "Python", icon: FaPython, color: "#3776AB", darkColor: "#3776AB", monacoLang: "python", ext: "py" },
  { id: "javascript", label: "JavaScript", icon: FaJsSquare, color: "#F7DF1E", darkColor: "#C7AF1E", monacoLang: "javascript", ext: "js" },
  { id: "arduino", label: "Arduino C++", icon: SiArduino, color: "#00979D", darkColor: "#00979D", monacoLang: "cpp", ext: "ino" },
  { id: "cpp", label: "C++", icon: TbBrandCpp, color: "#00599C", darkColor: "#00599C", monacoLang: "cpp", ext: "cpp" },
];

const getSourceEnvDisplayName = (env) => {
  if (env === "scratch" || env === "junior") return "Scratch Visual Blocks";
  if (env === "python") return "Python Workspace";
  if (env === "javascript") return "JavaScript Workspace";
  return "Workspace Code";
};

const UnifiedConvertCodePanel = ({
  sourceEnv = "python",
  sourceCode = "",
  initialTargetLang = "python",
  showSyncButton = true,
  onSync = null,
  onCodeSynchronized = null,
  onSaveSnippet = null,
  onBack = null,
  theme = "dark",
  fontSize = 14,
  className = "",
}) => {
  const [targetLang, setTargetLang] = useState(initialTargetLang);
  const [copied, setCopied] = useState(false);
  const [currentCode, setCurrentCode] = useState("");
  const [syncStatus, setSyncStatus] = useState("synced"); // "synced" | "syncing" | "error"
  const [syncLogs, setSyncLogs] = useState([
    {
      time: new Date().toLocaleTimeString(),
      type: "info",
      text:
        sourceEnv === "scratch" || sourceEnv === "junior"
          ? "Sync & Diagnostics engine ready. Live bidirectional synchronization with Scratch blocks active."
          : `Sync & Diagnostics engine ready. Live bidirectional synchronization with ${getSourceEnvDisplayName(sourceEnv)} active.`,
    },
  ]);
  const [activeError, setActiveError] = useState(null);
  const [isConsoleOpen, setIsConsoleOpen] = useState(true);
  const [panelHeight, setPanelHeight] = useState(160);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastSourceCodeRef = useRef(sourceCode);
  const isEditingRef = useRef(false);
  const onSaveSnippetRef = useRef(onSaveSnippet);
  const targetLangRef = useRef(targetLang);

  useEffect(() => {
    onSaveSnippetRef.current = onSaveSnippet;
  }, [onSaveSnippet]);

  useEffect(() => {
    targetLangRef.current = targetLang;
  }, [targetLang]);

  const isLight = theme === "light";
  const currentTargetObj = TARGET_LANGUAGES.find((t) => t.id === targetLang) || TARGET_LANGUAGES[0];

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);

    const onMouseMove = (moveEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const maxHeight = rect.height * 0.5; // Max 1/2 total height
      const newH = rect.bottom - moveEvent.clientY;

      if (newH < 45) {
        setIsConsoleOpen(false);
      } else {
        setIsConsoleOpen(true);
        setPanelHeight(Math.min(newH, maxHeight));
      }
    };

    const onMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Transpile when sourceEnv or targetLang tab changes
  useEffect(() => {
    const transpiled = transpileCode({
      sourceEnv,
      targetLang,
      sourceCode,
    });
    setCurrentCode(transpiled);
    lastSourceCodeRef.current = sourceCode;
    setSyncStatus("synced");
    setActiveError(null);
  }, [sourceEnv, targetLang]);

  // Transpile when sourceCode updates from outside (not from local edits)
  useEffect(() => {
    if (!isEditingRef.current && sourceCode !== lastSourceCodeRef.current) {
      const transpiled = transpileCode({
        sourceEnv,
        targetLang,
        sourceCode,
      });
      setCurrentCode(transpiled);
      lastSourceCodeRef.current = sourceCode;
      setSyncStatus("synced");
      setActiveError(null);
    }
  }, [sourceCode, sourceEnv, targetLang]);

  const addLog = (type, text, tip = null, line = null) => {
    setSyncLogs((prev) => [
      ...prev.slice(-40),
      {
        time: new Date().toLocaleTimeString(),
        type,
        text,
        tip,
        line,
      },
    ]);
  };

  // Perform validation and bi-directional synchronization
  const executeSync = (codeToSync, lang = targetLang) => {
    setSyncStatus("syncing");
    const targetLabel = TARGET_LANGUAGES.find((t) => t.id === lang)?.label || lang.toUpperCase();
    addLog("info", `🔍 Validating syntax for ${targetLabel}...`);

    const validation = validateSyntax(codeToSync, lang);

    if (!validation.isValid && validation.errors && validation.errors.length > 0) {
      const firstErr = validation.errors[0];
      setSyncStatus("error");
      setActiveError(firstErr);
      addLog("error", `⚠️ [Line ${firstErr.line}] ${firstErr.message}`, firstErr.tip, firstErr.line);
      return false;
    }

    // Syntax is valid -> Sync back to source workspace
    setSyncStatus("synced");
    setActiveError(null);
    const syncTargetText =
      sourceEnv === "scratch" || sourceEnv === "junior"
        ? "Visual blocks synchronized!"
        : `${getSourceEnvDisplayName(sourceEnv)} synchronized!`;
    addLog("success", `✨ Code validated successfully. ${syncTargetText}`);

    if (onCodeSynchronized) {
      onCodeSynchronized(codeToSync, lang);
    } else if (onSync) {
      onSync(codeToSync, lang);
    }
    return true;
  };

  // Handle live user editing with 8000ms (8s) debounce
  const handleEditorChange = (newValue) => {
    const val = newValue || "";
    setCurrentCode(val);
    isEditingRef.current = true;
    setSyncStatus("syncing");

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      executeSync(val, targetLang);
      isEditingRef.current = false;
    }, 8000);
  };

  const handleManualSyncClick = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    addLog("info", "🔄 Manual Sync triggered by user...");
    executeSync(currentCode, targetLang);
    isEditingRef.current = false;
  };

  const handleCopy = () => {
    if (!currentCode) return;
    navigator.clipboard.writeText(currentCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!currentCode) return;
    const standaloneCode = getStandaloneDownloadCode({
      sourceEnv,
      targetLang,
      code: currentCode,
    });
    const element = document.createElement("a");
    const file = new Blob([standaloneCode], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `converted_code.${currentTargetObj.ext}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleSaveSnippet = () => {
    const editor = editorRef.current;
    let codeToSave = currentCode;
    if (editor) {
      const selection = editor.getSelection();
      const selectedText = editor.getModel()?.getValueInRange(selection);
      if (selectedText && selectedText.trim()) {
        codeToSave = selectedText;
      }
    }

    if (!codeToSave || !codeToSave.trim()) {
      return;
    }

    const activeTargetId = targetLangRef.current || targetLang;
    const activeTarget = TARGET_LANGUAGES.find((t) => t.id === activeTargetId) || TARGET_LANGUAGES[0];

    const payload = {
      code: codeToSave,
      language: activeTarget.id,
      environment: activeTarget.id === "python" ? "python" : activeTarget.id === "javascript" ? "javascript" : "python",
      type: "code_snippet",
      defaultName: `Converted ${activeTarget.label} Snippet`,
      name: `Converted ${activeTarget.label} Snippet`,
    };

    if (onSaveSnippetRef.current) {
      onSaveSnippetRef.current(payload);
    } else {
      window.dispatchEvent(
        new CustomEvent("trigger-save-backpack-snippet", {
          detail: payload,
        })
      );
    }
  };

  // Broadcast active context language whenever targetLang changes
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("workspace-active-context-changed", {
        detail: {
          tab: "convert",
          language: targetLang,
          environment: targetLang,
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("workspace-active-context-changed", {
          detail: {
            tab: "editor",
            language: sourceEnv,
            environment: sourceEnv,
          },
        })
      );
    };
  }, [targetLang, sourceEnv]);

  // Support inserting backpack snippets directly at cursor in Convert Code Monaco editor
  useEffect(() => {
    const handleInsertSnippetInConvertPanel = (e) => {
      const snippet = e.detail;
      if (!snippet) return;
      const snippetText = snippet.code !== undefined && snippet.code !== null ? snippet.code : "";
      if (!snippetText) return;

      const editor = editorRef.current;
      if (editor) {
        const selection = editor.getSelection();
        if (selection) {
          editor.executeEdits("backpack-insert", [
            {
              range: selection,
              text: snippetText,
              forceMoveMarkers: true,
            },
          ]);
          editor.focus();
        } else {
          const currentVal = editor.getValue();
          const newVal = currentVal ? `${currentVal}\n\n${snippetText}` : snippetText;
          editor.setValue(newVal);
          editor.focus();
        }
        const updated = editor.getValue();
        setCurrentCode(updated);
        handleEditorChange(updated);
      } else {
        setCurrentCode((prev) => {
          const next = prev ? `${prev}\n\n${snippetText}` : snippetText;
          handleEditorChange(next);
          return next;
        });
      }
    };

    window.addEventListener("insert-backpack-code-snippet", handleInsertSnippetInConvertPanel);
    return () => window.removeEventListener("insert-backpack-code-snippet", handleInsertSnippetInConvertPanel);
  }, [handleEditorChange]);

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;

    // Disable Undo / Redo keybindings (Ctrl+Z, Ctrl+Y, Cmd+Z, Cmd+Shift+Z)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ, () => null);
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyY, () => null);
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyZ, () => null);

    // Save project shortcut (Ctrl+S / Cmd+S)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      window.dispatchEvent(new CustomEvent("workspace-save-shortcut"));
    });

    // Save snippet shortcut & context menu item (Ctrl+Shift+B / Cmd+Shift+B)
    editor.addAction({
      id: "save-converted-to-backpack-snippet",
      label: "Save as Snippet to Backpack",
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyB,
      ],
      contextMenuGroupId: "9_backpack",
      contextMenuOrder: 1,
      run: () => {
        handleSaveSnippet();
      },
    });
  };

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col font-sans relative z-20 overflow-hidden ${
        isLight ? "bg-[#ffffff] text-slate-800" : "bg-[#1e1e1e] text-white"
      } ${className}`}
    >
      {/* Header Toolbar */}
      <div
        className={`px-3.5 py-1.5 flex items-center justify-between gap-3 text-xs flex-wrap flex-shrink-0 ${
          isLight ? "bg-[#f8fafc] border-b border-[#e2e8f0]" : "bg-[#21262d] border-b border-[#30363d]"
        }`}
      >
        {/* Left Side: Back Button & Language Pill Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className={`p-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer mr-1 ${
                isLight ? "bg-slate-200 hover:bg-slate-300 text-slate-700" : "bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white"
              }`}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back</span>
            </button>
          )}

          {TARGET_LANGUAGES.map((lang) => {
            const Icon = lang.icon;
            const isActive = targetLang === lang.id;
            const activeBg = isLight ? lang.color : (lang.darkColor || lang.color);
            const activeTextColor = isLight && lang.id === "javascript" ? "#000000" : "#ffffff";

            return (
              <button
                key={lang.id}
                type="button"
                title={`Switch to ${lang.label} view`}
                onClick={() => {
                  setTargetLang(lang.id);
                  const transpiled = transpileCode({
                    sourceEnv,
                    targetLang: lang.id,
                    sourceCode,
                  });
                  setCurrentCode(transpiled);
                  setSyncStatus("synced");
                  setActiveError(null);
                  addLog("info", `Switched target language to ${lang.label}.`);
                }}
                style={
                  isActive
                    ? {
                        backgroundColor: activeBg,
                        color: activeTextColor,
                      }
                    : undefined
                }
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-0 ${
                  isActive
                    ? "shadow-md scale-[1.02]"
                    : isLight
                    ? "bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold"
                    : "bg-white/5 hover:bg-white/10 text-slate-400 hover:text-slate-200 font-semibold"
                }`}
              >
                <Icon style={{ color: isActive ? activeTextColor : lang.color }} className="w-4 h-4" />
                <span className={isActive ? "font-extrabold" : ""}>{lang.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right Side: Single Sync Button & Copy / Download Buttons */}
        <div className="flex items-center gap-2">
          {showSyncButton && (
            <button
              type="button"
              onClick={handleManualSyncClick}
              disabled={syncStatus === "syncing"}
              title="Validate and sync converted code with workspace"
              className={`px-3.5 py-1 rounded-lg text-xs font-extrabold transition flex items-center gap-1.5 shadow-xs ${
                syncStatus === "syncing"
                  ? isLight
                    ? "bg-amber-100 text-amber-800 border border-amber-300 opacity-80 cursor-not-allowed"
                    : "bg-[#78350f] text-[#fcd34d] border-0 opacity-85 cursor-not-allowed"
                  : syncStatus === "error"
                  ? isLight
                    ? "bg-red-100 hover:bg-red-200 text-red-800 border border-red-300 cursor-pointer"
                    : "bg-[#7f1d1d] hover:bg-[#991b1b] text-[#fca5a5] border-0 cursor-pointer"
                  : isLight
                  ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300 cursor-pointer"
                  : "bg-[#064e3b] hover:bg-[#065f46] text-[#34d399] border-0 cursor-pointer"
              }`}
            >
              <LuRefreshCw
                className={`w-3.5 h-3.5 ${
                  syncStatus === "syncing"
                    ? "animate-spin text-[#fcd34d]"
                    : syncStatus === "error"
                    ? "text-[#fca5a5]"
                    : "text-[#34d399]"
                }`}
              />
              <span>{syncStatus === "syncing" ? "Synchronizing..." : "Sync"}</span>
            </button>
          )}

          {sourceEnv !== "scratch" && sourceEnv !== "junior" && (
            <>
              <button
                type="button"
                onClick={handleCopy}
                title={copied ? "Copied to clipboard!" : `Copy ${currentTargetObj.label} code`}
                className={`px-3.5 py-1 rounded-lg text-xs font-extrabold shadow-xs flex items-center gap-1.5 transition cursor-pointer ${
                  isLight
                    ? "bg-white hover:bg-slate-100 text-slate-700 border border-slate-300"
                    : "bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border-0"
                }`}
              >
                {copied ? (
                  <>
                    <LuCheck className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-emerald-500 font-black">Copied!</span>
                  </>
                ) : (
                  <>
                    <LuCopy className="w-3.5 h-3.5" />
                    <span>Copy</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleDownload}
                title={`Download .${currentTargetObj.ext} (${currentTargetObj.label})`}
                className={`px-3.5 py-1 rounded-lg text-xs font-extrabold shadow-xs flex items-center gap-1.5 transition cursor-pointer ${
                  isLight
                    ? "bg-white hover:bg-slate-100 text-slate-700 border border-slate-300"
                    : "bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border-0"
                }`}
              >
                <LuDownload className="w-3.5 h-3.5" />
                <span>Download</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Monaco Code Editor Area */}
      <div className="flex-1 w-full relative min-h-0">
        <Editor
          height="100%"
          language={currentTargetObj.monacoLang}
          theme={isLight ? "vs" : "vs-dark"}
          value={currentCode}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          options={{
            fontSize: fontSize,
            fontFamily: "Fira Code, Consolas, Monaco, monospace",
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: "on",
            automaticLayout: true,
            tabSize: 4,
            readOnly: false,
          }}
        />
      </div>

      {/* Bottom Panel: Sync & Diagnostics Console (Resizable) */}
      <div
        style={{ height: isConsoleOpen ? `${panelHeight}px` : "36px" }}
        className={`border-t flex flex-col flex-shrink-0 relative ${
          isDragging ? "" : "transition-[height] duration-150"
        } ${
          isLight ? "bg-[#f8fafc] border-[#e2e8f0]" : "bg-[#181818] border-[#30363d]"
        }`}
      >
        {/* Resizer Drag Handle at Top Edge */}
        <div
          onMouseDown={handleResizeMouseDown}
          className="h-2 w-full cursor-row-resize absolute -top-1 left-0 right-0 z-30 group flex items-center justify-center select-none"
          title="Drag to resize panel (Min: 0, Max: 50%)"
        >
          <div className="w-12 h-1 rounded-full bg-slate-300 dark:bg-slate-700 group-hover:bg-indigo-400 group-active:bg-indigo-500 transition-colors" />
        </div>

        {/* Panel Header */}
        <div
          className={`px-3.5 py-1.5 flex items-center justify-between text-xs cursor-pointer select-none ${
            isLight ? "bg-[#f1f5f9] text-slate-800" : "bg-[#21262d] text-slate-200"
          }`}
          onClick={() => setIsConsoleOpen(!isConsoleOpen)}
        >
          <div className="flex items-center gap-2.5 overflow-hidden">
            <Terminal className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
            <span className="font-extrabold text-xs flex-shrink-0">Sync & Diagnostics</span>
            {!isConsoleOpen && (
              <div className="flex items-center gap-1.5 text-[11px] font-semibold truncate">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    syncStatus === "syncing"
                      ? "bg-amber-400 animate-pulse shadow-[0_0_6px_rgba(251,191,36,0.6)]"
                      : syncStatus === "error"
                      ? "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]"
                      : "bg-[#10b981] shadow-[0_0_6px_rgba(16,185,129,0.6)]"
                  }`}
                />
                <span
                  className={
                    syncStatus === "syncing"
                      ? isLight
                        ? "text-amber-700 font-bold"
                        : "text-[#fcd34d] font-bold"
                      : syncStatus === "error"
                      ? "text-red-500 font-bold"
                      : isLight
                      ? "text-emerald-700 font-bold"
                      : "text-[#34d399] font-bold"
                  }
                >
                  {syncStatus === "syncing"
                    ? "Validating & Synchronizing..."
                    : syncStatus === "error"
                    ? "Syntax error detected"
                    : "Code synchronized"}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            {syncStatus === "error" && activeError && (
              <span
                className={`px-2.5 py-0.5 rounded-md text-[10px] font-black flex items-center gap-1 ${
                  isLight
                    ? "bg-red-100 text-red-700 border border-red-200"
                    : "bg-red-500/20 text-red-400 border-0"
                }`}
              >
                <AlertCircle className="w-3 h-3 text-red-400" />
                Line {activeError.line} Error
              </span>
            )}

            <button
              type="button"
              className="p-1 text-slate-400 hover:text-slate-200 transition cursor-pointer"
              title={isConsoleOpen ? "Collapse Diagnostics" : "Expand Diagnostics"}
            >
              {isConsoleOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Panel Body: Activity Stream & Educational Diagnostics */}
        {isConsoleOpen && (
          <div
            className={`flex-1 p-3 overflow-y-auto font-mono text-xs space-y-1.5 select-text ${
              isLight ? "bg-white text-slate-800" : "bg-[#181818] text-slate-300"
            }`}
          >
            {syncLogs.map((log, i) => (
              <div key={i} className="leading-relaxed">
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 text-[10px] flex-shrink-0 select-none">[{log.time}]</span>
                  <span
                    className={`flex-1 break-all ${
                      log.type === "error"
                        ? "text-[#f87171] font-bold"
                        : log.type === "success"
                        ? isLight
                          ? "text-emerald-700 font-bold"
                          : "text-[#34d399] font-bold"
                        : log.type === "warning"
                        ? isLight
                          ? "text-amber-700 font-semibold"
                          : "text-[#fcd34d]"
                        : isLight
                        ? "text-slate-800 font-medium"
                        : "text-slate-200"
                    }`}
                  >
                    {log.text}
                  </span>
                </div>

                {/* Educational Diagnostic Tip Box */}
                {log.tip && (
                  <div className="ml-16 mt-1 p-2 rounded-lg bg-amber-950/40 border border-amber-500/30 text-amber-300 text-[11px] flex items-start gap-2">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="font-sans leading-tight">{log.tip}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedConvertCodePanel;

